import { Box, Button, Flex, Input, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import {
  getAllExams,
  getManualExams,
} from "../../../modules/admin-modules/exams/Api";
import { getTopStudentsRequest } from "../../../modules/admin-modules/students/Actions";

const scoreOf = (student, examId) => {
  const scores = student?.exam_scores || {};
  const value = scores[examId] ?? scores[String(examId)];
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const TopStudents = () => {
  const dispatch = useDispatch();
  const [groups, setGroups] = useState([[]]);
  const [reportGroups, setReportGroups] = useState([]);
  const [combinedExams, setCombinedExams] = useState([]);
  const [topN, setTopN] = useState(5);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);

  const topStudents = useSelector((state) => state.students.topStudents);
  const isLoading = useSelector((state) => state.students.isLoading);

  useEffect(() => {
    let cancelled = false;
    const loadExams = async () => {
      try {
        const [allRes, manualRes] = await Promise.all([
          getAllExams(),
          getManualExams(),
        ]);
        if (cancelled) return;
        const all = allRes?.data?.data || [];
        const manual = manualRes?.data?.data || [];
        const manualIds = new Set(manual.map((exam) => exam?.id).filter((id) => id != null));
        const byId = new Map();
        [...all, ...manual].forEach((exam) => {
          if (exam?.id == null) return;
          byId.set(exam.id, {
            ...exam,
            isManual: manualIds.has(exam.id),
          });
        });
        const getCreatedTime = (exam) => {
          const time = Date.parse(exam?.created_at);
          return Number.isNaN(time) ? null : time;
        };
        setCombinedExams(
          Array.from(byId.values()).sort((a, b) => {
            const timeA = getCreatedTime(a);
            const timeB = getCreatedTime(b);
            if (timeA != null && timeB != null && timeA !== timeB) {
              return timeA - timeB;
            }
            if (timeA != null && timeB == null) return -1;
            if (timeA == null && timeB != null) return 1;
            return (a.id || 0) - (b.id || 0);
          })
        );
      } catch (error) {
        // keep empty options on failure
      }
    };
    loadExams();
    return () => {
      cancelled = true;
    };
  }, []);

  const examsOptions = combinedExams?.map((data) => ({
    value: data.id,
    label: data.isManual ? `${data.name} (سنتر)` : data.name,
  }));

  const filledGroups = groups.filter((group) => group.length > 0);

  const formulaPreview = filledGroups
    .map((group) => {
      const names = group.map((exam) => exam.label);
      return names.length > 1 ? `max(${names.join(", ")})` : names[0];
    })
    .join(" + ");

  const optionsForGroup = (groupIndex) => {
    const used = new Set();
    groups.forEach((group, index) => {
      if (index === groupIndex) return;
      group.forEach((exam) => used.add(String(exam.value)));
    });
    return (examsOptions || []).filter((option) => !used.has(String(option.value)));
  };

  const updateGroup = (groupIndex, nextGroup) => {
    setGroups((current) =>
      current.map((group, index) => (index === groupIndex ? nextGroup || [] : group))
    );
  };

  const addGroup = () => {
    setGroups((current) => [...current, []]);
  };

  const removeGroup = (groupIndex) => {
    setGroups((current) =>
      current.length === 1 ? current : current.filter((_, index) => index !== groupIndex)
    );
  };

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      minWidth: "220px",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
    menu: (styles) => ({ ...styles, zIndex: 9999 }),
    menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
  };

  const handleFetch = () => {
    if (!filledGroups.length) return;
    const nextGroups = filledGroups.map((group) => [...group]);
    setReportGroups(nextGroups);
    setPageNumber(0);
    const formData = {
      groups: nextGroups.map((group) => group.map((exam) => exam.value)),
      exams: nextGroups.flat().map((exam) => exam.value),
    };
    if (topN) {
      formData.top_n = Number(topN);
    }
    dispatch(getTopStudentsRequest(formData));
  };

  const examColumns = useMemo(() => {
    const columns = [];
    reportGroups.forEach((group, groupIndex) => {
      group.forEach((exam) => {
        columns.push({
          Header: exam.label,
          accessor: `exam_${exam.value}`,
        });
      });
      if (group.length > 1) {
        columns.push({
          Header: `max(${group.map((exam) => exam.label).join(", ")})`,
          accessor: `group_max_${groupIndex}`,
        });
      }
    });
    return columns;
  }, [reportGroups]);

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "اسم الطالب", accessor: "name" },
    { Header: "كود الطالب", accessor: "username" },
    { Header: "رقم الطالب", accessor: "phone" },
    { Header: "رقم ولي الأمر", accessor: "parent_phone" },
    { Header: "المجموعة", accessor: "group" },
    ...examColumns,
    { Header: "المجموع", accessor: "total_right_answers" },
  ];

  const Data = useMemo(() => {
    if (!reportGroups.length) return [];
    const rows = (topStudents || []).map((data) => {
      const row = {
        id: data?.student_id,
        name: data?.name,
        username: data?.username,
        phone: data?.phone,
        parent_phone: data?.parent_phone,
        group: data?.group || "",
      };
      let total = 0;
      reportGroups.forEach((group, groupIndex) => {
        let max = 0;
        group.forEach((exam) => {
          const score = scoreOf(data, exam.value);
          row[`exam_${exam.value}`] = score;
          max = Math.max(max, score);
        });
        if (group.length > 1) {
          row[`group_max_${groupIndex}`] = max;
        }
        total += max;
      });
      row.total_right_answers = total;
      return row;
    });
    rows.sort((a, b) => b.total_right_answers - a.total_right_answers);
    return rows.map((row, index) => ({
      ...row,
      index: `#${index + 1}`,
    }));
  }, [topStudents, reportGroups]);

  const headers = [
    { label: "name", key: "name" },
    { label: "code", key: "username" },
    { label: "phone", key: "phone" },
    { label: "parent_phone", key: "parent_phone" },
    { label: "group", key: "group" },
    ...examColumns.map((col) => ({ label: col.Header, key: col.accessor })),
    { label: "total", key: "total_right_answers" },
  ];

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="100"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            الطلاب المتمييزين
          </Text>
          <LogoutMenu />
        </Flex>

        <Box mb={6}>
          <Text fontSize="sm" color="gray.600" mb={3}>
            الامتحانات داخل المجموعة الواحدة يُحسب منها الأعلى فقط، ثم تُجمع المجموعات. من لم يحضر امتحاناً تُحسب درجته 0.
          </Text>
          <VStack align="stretch" spacing={3}>
            {groups.map((group, index) => (
              <Flex key={index} gap={2} align="center" flexWrap="wrap">
                <Text minW="90px" fontWeight="600" color="main_1">
                  مجموعة {index + 1}
                </Text>
                <Box flex="1" minW="220px">
                  <Select
                    options={optionsForGroup(index)}
                    isMulti
                    closeMenuOnSelect={false}
                    value={group}
                    onChange={(data) => updateGroup(index, data || [])}
                    styles={colourStyles}
                    placeholder="اختر الامتحانات"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </Box>
                <Button
                  variant="outline"
                  colorScheme="red"
                  borderRadius="16px"
                  onClick={() => removeGroup(index)}
                  isDisabled={groups.length === 1}
                >
                  حذف
                </Button>
              </Flex>
            ))}
          </VStack>
          <Flex mt={3} gap={2} align="center" flexWrap="wrap">
            <Button variant="outline" borderRadius="16px" onClick={addGroup}>
              إضافة مجموعة
            </Button>
            {formulaPreview ? (
              <Text fontSize="sm" color="gray.700">
                الحساب: {formulaPreview}
              </Text>
            ) : null}
          </Flex>
        </Box>

        <Flex mb={4} gap={2} align="center" flexWrap="wrap">
            <Input
              type="number"
              min={1}
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
              placeholder="Top N"
              w="100px"
              bg="#f5f5f5"
              borderRadius="16px"
            />
            <Button
              colorScheme="red"
              borderRadius="16px"
              onClick={handleFetch}
              isDisabled={!filledGroups.length}
              isLoading={isLoading}
            >
              عرض
            </Button>
            <Box border="1px solid gray" borderRadius="8px" padding="10px">
              <CSVLink
                headers={headers}
                data={Data?.length != 0 ? Data : []}
                filename={"top-students.csv"}
                className="btn btn-primary"
                target="_blank"
              >
                Download
              </CSVLink>
            </Box>
        </Flex>

        <CTable
          selectedData={() => {}}
          Columns={columns}
          Data={Data}
          path={"openModel"}
          Actions
          Title={``}
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar={true}
          noFilter={true}
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar={true}
          isLoading={isLoading}
          perPage={size}
          totalPage={Math.ceil((Data?.length || 0) / size) || 1}
        />
      </div>
    </Layout>
  );
};

export default TopStudents;
